import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authApi } from '../api/auth.ts';
import { ApiError } from '../api/client.ts';
import { useAuthStore } from '../state/authStore.ts';
import { useSeo } from '../hooks/useSeo.ts';

type Phase = 'verifying' | 'success' | 'invalid';

/**
 * Landing page for the email verification link. Consumes the token, signs the user in
 * (the backend issues the auth cookie on success), and routes to the home page so they
 * can run their first scan.
 */
export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const setUser = useAuthStore(s => s.setUser);
  const triedRef = useRef(false);
  const [phase, setPhase] = useState<Phase>('verifying');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useSeo({ title: 'Confirming your email', canonicalPath: '/verify-email' });

  useEffect(() => {
    // StrictMode double-mounts in dev — the token is single-use, so a second attempt would
    // always 400. Gate with a ref to ensure the network call only fires once per real mount.
    if (triedRef.current) return;
    triedRef.current = true;

    if (!token) {
      setPhase('invalid');
      setErrorMessage('No token in the link. Open the email and click the button again.');
      return;
    }

    (async () => {
      try {
        await authApi.verifyEmail(token);
        const me = await authApi.me();
        setUser(me);
        setPhase('success');
        // Auto-redirect to home after a beat so the user sees confirmation, then lands ready
        // to scan. They can click through immediately if they prefer.
        setTimeout(() => navigate('/'), 1500);
      } catch (err) {
        setPhase('invalid');
        if (err instanceof ApiError) {
          setErrorMessage(err.detail || err.title);
        } else {
          setErrorMessage('Something went wrong confirming your email.');
        }
      }
    })();
  }, [token, navigate, setUser]);

  if (phase === 'verifying') {
    return (
      <div className="sign-in">
        <div className="spinner" aria-hidden="true" />
        <h1>Confirming your email…</h1>
      </div>
    );
  }

  if (phase === 'invalid') {
    return (
      <div className="sign-in">
        <h1>This link can't be used</h1>
        <p className="error">{errorMessage ?? 'The verification link is invalid or expired.'}</p>
        <p className="muted">
          <Link to="/sign-in">Go to sign-in</Link> and use the "Resend it" link to get a fresh email.
        </p>
      </div>
    );
  }

  return (
    <div className="sign-in">
      <h1>Email confirmed</h1>
      <p className="muted">You're signed in. Taking you to the scanner…</p>
      <Link to="/" className="cta">Run a scan</Link>
    </div>
  );
}
